<template>
  <div class="production-priority-page">
    <div class="pp-inner">
      <div class="priority-header">
        <div class="pp-title">
          <div class="bold-24">{{ $t("productionPriority.title") }}</div>
          <div class="regular-12 text-gray">{{ subLabel }}</div>
        </div>
        <div class="d-flex">
          <button
            class="btn btn-sm btn-no-r"
            :class="view === 'board' ? 'btn-primary' : 'btn-secondary'"
            @click="switchView('board')"
          >
            {{ $t("productionPriority.viewBoard") }}
          </button>
          <button
            class="btn btn-sm btn-no-l"
            :class="view === 'timeline' ? 'btn-primary' : 'btn-secondary'"
            @click="switchView('timeline')"
          >
            {{ $t("productionPriority.viewTimeline") }}
          </button>
        </div>
        <div class="flex-fill"></div>
        <div class="pp-stats" v-if="!loading && !errorMessage">
          <div class="pp-stat" v-for="st in boardStats" :key="st.label">
            <span class="pp-stat-label">{{ st.label }}</span>
            <span class="pp-stat-value" :style="{ color: st.color }">{{
              st.value
            }}</span>
          </div>
        </div>
        <button class="btn btn-sm btn-secondary pp-refresh" @click="loadData">
          <BaseIcon name="refresh" />
          <span>{{ $t("productionPriority.refresh") }}</span>
        </button>
      </div>

      <div v-if="loading" class="m-5 p-5 text-center bold-40">
        <div class="spinner-border spinner-border-sm me-2" role="status"></div>
        {{ $t("productionPriority.loading") }}
      </div>

      <div v-else-if="errorMessage" class="nature-white-box text-center py-4">
        <div class="alert alert-danger">{{ errorMessage }}</div>
        <button class="btn btn-sm btn-outline-danger" @click="loadData">
          {{ $t("productionPriority.retry") }}
        </button>
      </div>

      <div v-else-if="view === 'board'" class="board-scroll">
        <div class="board">
          <div
            v-for="column in columns"
            :key="column.column_key"
            class="board-column"
            :class="'board-column--' + column.type"
          >
            <div class="column-header">
              <div class="column-title">
                <span class="column-name" :title="column.name">{{
                  column.name
                }}</span>
                <span class="column-type" :class="'type-' + column.type">
                  {{ columnTypeLabel(column.type) }}
                </span>
                <button
                  v-if="column.type === 'partner'"
                  class="capacity-edit-btn"
                  :title="$t('productionPriority.editCapacity')"
                  @click="openCapacity(column)"
                >
                  <BaseIcon name="settings" />
                </button>
              </div>
              <div class="column-capacity">
                <template v-if="column.type === 'partner'">
                  <span v-if="column.capacity_hours">
                    {{
                      $t("productionPriority.capacityOf", {
                        used: capacityHours(column),
                        total: column.capacity_hours,
                      })
                    }}
                  </span>
                  <span v-else>{{ $t("productionPriority.noCapacity") }}</span>
                </template>
                <template v-else-if="column.type === 'warehouse'">
                  <span>
                    {{
                      $t("productionPriority.scheduledHours", {
                        hours: column.used_hours,
                      })
                    }}
                  </span>
                </template>
                <span class="cap-sep">&middot;</span>
                <span>
                  {{
                    $t("productionPriority.queuedHours", {
                      hours: queuedHours(column),
                    })
                  }}
                </span>
                <span class="column-count">{{
                  $t("productionPriority.cardCount", { n: column.cards.length })
                }}</span>
              </div>
              <div
                v-if="column.type === 'partner' && column.capacity_hours"
                class="capacity-bar"
              >
                <div
                  class="capacity-bar-fill"
                  :style="{
                    width: Math.min(100, capacityRatio(column) * 100) + '%',
                    background: capacityColor(column),
                  }"
                ></div>
                <span class="capacity-bar-mark"></span>
              </div>
            </div>

            <draggable
              v-model="column.cards"
              group="production-priority"
              item-key="id"
              class="column-body"
              :animation="150"
              @change="onColumnChange(column, $event)"
            >
              <template #item="{ element: card }">
                <div
                  class="priority-card"
                  :class="{ 'is-overdue': isOverdue(card) }"
                  :style="{ borderLeftColor: cardPriorityColor(card) }"
                >
                  <div class="card-top">
                    <span
                      class="card-priority"
                      :style="cardPriorityStyle(card)"
                      >{{ card.priority_label }}</span
                    >
                    <span class="card-id">{{ card.pretty_id }}</span>
                    <span v-if="isOverdue(card)" class="card-overdue-badge">{{
                      $t("productionPriority.overdue")
                    }}</span>
                  </div>
                  <div
                    v-for="item in card.items.slice(0, 2)"
                    :key="item.id"
                    class="card-item"
                  >
                    <img
                      v-if="item.product.image"
                      :src="productImageUrl(item.product.image)"
                      class="card-image"
                      alt=""
                    />
                    <BaseIcon v-else name="no-image" class="card-image" />
                    <div class="card-item-info">
                      <div class="card-item-name" :title="item.product.name">
                        {{ item.product.name }}
                      </div>
                      <div class="card-item-meta">
                        <span>
                          {{ $t("productionPriority.qty") }}:
                          {{ item.quantity }}
                        </span>
                        <span v-if="item.sku"> &middot; {{ item.sku }} </span>
                      </div>
                    </div>
                  </div>
                  <div v-if="card.items.length > 2" class="card-more">
                    {{
                      $t("productionPriority.moreItems", {
                        n: card.items.length - 2,
                      })
                    }}
                  </div>
                  <div class="card-footer">
                    <span
                      v-if="card.deadline_date"
                      class="card-deadline"
                      :class="{ 'is-overdue': isOverdue(card) }"
                    >
                      <BaseIcon name="calendar" />
                      {{ formatDate(card.deadline_date) }}
                    </span>
                    <span v-else class="card-deadline text-gray">–</span>
                    <span class="card-hours">{{ card.estimated_hours }} h</span>
                  </div>
                </div>
              </template>
              <template #footer>
                <div v-if="!column.cards.length" class="column-empty">
                  {{ $t("productionPriority.dropHere") }}
                </div>
              </template>
            </draggable>
          </div>
        </div>
      </div>

      <ProductionTimeline
        v-else-if="view === 'timeline'"
        :days="timelineDays"
        :boards="timelineBoards"
        :loading-more="timelineLoadingMore"
        @reload="reloadTimeline"
        @load-more="loadMoreTimeline"
      />
    </div>
    <Modal
      ref="capacityModal"
      :title="$t('productionPriority.capacityTitle')"
      className="modal-lg"
    >
      <div v-if="capacityColumn">
        <div v-if="capacityLoading" class="text-center py-4">
          <div
            class="spinner-border spinner-border-sm me-2"
            role="status"
          ></div>
          {{ $t("productionPriority.loading") }}
        </div>
        <div v-else>
          <div class="base-capacity-section">
            <div class="base-capacity-title">
              <h6 class="mb-1">
                {{ $t("productionPriority.baseCapacityTitle") }}
              </h6>
              <span class="text-muted small">
                {{ $t("productionPriority.baseCapacityHint") }}
              </span>
            </div>
            <div class="row g-2">
              <div class="col">
                <label class="form-label small mb-1">{{
                  $t("productionPriority.minimumHours")
                }}</label>
                <input
                  v-model.number="capacityBase.minimum_hours"
                  type="number"
                  min="0"
                  class="form-control form-control-sm"
                />
              </div>
              <div class="col">
                <label class="form-label small mb-1">{{
                  $t("productionPriority.expectedHours")
                }}</label>
                <input
                  v-model.number="capacityBase.expected_hours"
                  type="number"
                  min="0"
                  class="form-control form-control-sm"
                />
              </div>
              <div class="col">
                <label class="form-label small mb-1">{{
                  $t("productionPriority.maximumHours")
                }}</label>
                <input
                  v-model.number="capacityBase.maximum_hours"
                  type="number"
                  min="0"
                  class="form-control form-control-sm"
                />
              </div>
              <div class="col">
                <label class="form-label small mb-1">{{
                  $t("productionPriority.confidence")
                }}</label>
                <input
                  v-model.number="capacityBase.confidence"
                  type="number"
                  min="0"
                  max="1"
                  step="0.1"
                  class="form-control form-control-sm"
                />
              </div>
            </div>
          </div>
          <div class="capacity-weeks-title">
            <h6 class="mb-2">
              {{ $t("productionPriority.weeklyCapacityTitle") }}
            </h6>
          </div>
          <div class="table-responsive">
            <table class="table table-sm table-bordered align-middle mb-0">
              <thead>
                <tr>
                  <th>{{ $t("productionPriority.week") }}</th>
                  <th>{{ $t("productionPriority.minimumHours") }}</th>
                  <th>{{ $t("productionPriority.expectedHours") }}</th>
                  <th>{{ $t("productionPriority.maximumHours") }}</th>
                  <th>{{ $t("productionPriority.confidence") }}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="week in capacityWeeks"
                  :key="week.week_start"
                  :class="{ 'week-is-base': !week.custom }"
                >
                  <td class="text-nowrap">
                    {{ formatDate(week.week_start) }}
                    <span
                      v-if="!week.custom"
                      class="badge bg-light text-muted ms-1"
                      >{{ $t("productionPriority.fromBase") }}</span
                    >
                  </td>
                  <td>
                    <input
                      v-model.number="week.minimum_hours"
                      type="number"
                      min="0"
                      class="form-control form-control-sm"
                      @input="markWeekCustom(week)"
                    />
                  </td>
                  <td>
                    <input
                      v-model.number="week.expected_hours"
                      type="number"
                      min="0"
                      class="form-control form-control-sm"
                      @input="markWeekCustom(week)"
                    />
                  </td>
                  <td>
                    <input
                      v-model.number="week.maximum_hours"
                      type="number"
                      min="0"
                      class="form-control form-control-sm"
                      @input="markWeekCustom(week)"
                    />
                  </td>
                  <td>
                    <input
                      v-model.number="week.confidence"
                      type="number"
                      min="0"
                      max="1"
                      step="0.1"
                      class="form-control form-control-sm"
                      @input="markWeekCustom(week)"
                    />
                  </td>
                  <td class="text-center">
                    <button
                      v-if="week.forecast_id"
                      class="btn btn-sm btn-outline-danger"
                      :title="$t('productionPriority.delete')"
                      :disabled="capacitySaving"
                      @click="deleteCapacityWeek(week)"
                    >
                      <BaseIcon name="trash" />
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div class="d-flex justify-content-end gap-2 mt-3">
          <button
            class="btn btn-sm btn-outline-secondary"
            :disabled="capacitySaving"
            @click="closeCapacity"
          >
            {{ $t("productionPriority.cancel") }}
          </button>
          <button
            class="btn btn-sm btn-primary"
            :disabled="capacitySaving"
            @click="saveCapacity"
          >
            <span
              v-if="capacitySaving"
              class="spinner-border spinner-border-sm me-1"
            ></span>
            {{ $t("productionPriority.save") }}
          </button>
        </div>
      </div>
    </Modal>
  </div>
</template>

<script>
import dayjs from "dayjs";
import draggable from "vuedraggable";
import BaseIcon from "@/components/icons/BaseIcon";
import Modal from "@/components/modals/Modal.vue";
import ProductionTimeline from "./ProductionTimeline.vue";
import http from "@/modules/http";
import tenant from "@/modules/tenant";

export default {
  name: "ProductionPriority",
  components: { draggable, BaseIcon, Modal, ProductionTimeline },
  data() {
    return {
      loading: true,
      errorMessage: "",
      view: "timeline",
      columns: [],
      timelineDays: [],
      timelineBoards: [],
      timelineRangeStart: "",
      timelineRangeEnd: "",
      timelineLoadingMore: false,
      capacityColumn: null,
      capacityWeeks: [],
      capacityBase: {
        minimum_hours: "",
        expected_hours: "",
        maximum_hours: "",
        confidence: "",
      },
      capacityLoading: false,
      capacitySaving: false,
    };
  },
  computed: {
    mediaServer() {
      return tenant.getAppMediaUrl();
    },
    subLabel() {
      return this.$t("productionPriority.subLabel", {
        n: this.columns.length,
      });
    },
    // fejléc-mutatók a látható oszlopokból
    boardStats() {
      const cards = this.columns.reduce((a, c) => a + c.cards.length, 0);
      const hours = this.columns.reduce((a, c) => a + this.queuedHours(c), 0);
      const overdue = this.columns.reduce(
        (a, c) => a + c.cards.filter((x) => this.isOverdue(x)).length,
        0
      );
      const withCap = this.columns.filter(
        (c) => c.type === "partner" && c.capacity_hours
      );
      const capSum = withCap.reduce((a, c) => a + Number(c.capacity_hours), 0);
      const usedSum = withCap.reduce((a, c) => a + this.capacityHours(c), 0);
      return [
        {
          label: this.$t("productionPriority.statCards"),
          value: String(cards),
        },
        {
          label: this.$t("productionPriority.statQueued"),
          value: Math.round(hours) + " h",
        },
        {
          label: this.$t("productionPriority.statOverdue"),
          value: String(overdue),
          color: overdue > 0 ? "var(--red)" : "var(--green)",
        },
        {
          label: this.$t("productionPriority.statUtilization"),
          value: capSum ? Math.round((usedSum / capSum) * 100) + "%" : "–",
          color: usedSum / capSum > 1 ? "var(--red)" : "var(--green)",
        },
      ];
    },
  },
  watch: {
    capacityBase: {
      deep: true,
      handler() {
        if (!this.capacityColumn) return;
        this.capacityWeeks.forEach((week) => {
          if (week.custom) return;
          week.minimum_hours = this.capacityBase.minimum_hours ?? "";
          week.expected_hours = this.capacityBase.expected_hours ?? "";
          week.maximum_hours = this.capacityBase.maximum_hours ?? "";
          week.confidence = this.capacityBase.confidence ?? "";
        });
      },
    },
  },
  mounted() {
    this.loadData();
  },
  methods: {
    capacityColor(column) {
      const r = this.capacityRatio(column);
      if (r > 1) return "var(--red)";
      if (r > 0.7) return "var(--yellow)";
      return "var(--green)";
    },
    // a kártya sürgőssége: lejárt határidő > 3 napon belül > egyéb
    cardPriorityLevel(card) {
      if (this.isOverdue(card)) return "high";
      if (!card.deadline_date) return "low";
      const days = dayjs(card.deadline_date).diff(
        dayjs().startOf("day"),
        "day"
      );
      return days <= 3 ? "mid" : "low";
    },
    cardPriorityColor(card) {
      const map = {
        high: "var(--red)",
        mid: "var(--yellow)",
        low: "var(--divider)",
      };
      return map[this.cardPriorityLevel(card)];
    },
    cardPriorityStyle(card) {
      const map = {
        high: { background: "#FFE3E3", color: "#B3261E" },
        mid: { background: "var(--lyellow)", color: "#8A6A00" },
        low: { background: "var(--bg)", color: "var(--gray)" },
      };
      return map[this.cardPriorityLevel(card)];
    },
    loadData(options = {}) {
      const silent = !!(options && options.silent);
      if (!silent) this.loading = true;
      this.errorMessage = "";
      let url =
        this.view === "timeline"
          ? "/production/priority/timeline"
          : "/production/priority/board";
      if (this.view === "timeline") {
        if (!this.timelineRangeStart) {
          this.timelineRangeStart = dayjs()
            .startOf("week")
            .format("YYYY-MM-DD");
          // ~6 hét látszik egyszerre; jobbra görgetve a loadMoreTimeline
          // bővíti 28 naponként (12 hónap egyben olvashatatlan rácsot adott)
          this.timelineRangeEnd = dayjs(this.timelineRangeStart)
            .add(45, "day")
            .format("YYYY-MM-DD");
        }
        url += `?from=${this.timelineRangeStart}&to=${this.timelineRangeEnd}`;
      }
      http
        .fetch(url)
        .then((data) => {
          if (!data) return;
          if (this.view === "timeline") {
            this.timelineDays = data.days || [];
            this.timelineBoards = data.boards || [];
          } else {
            this.buildBoard(data.columns || [], data.cards || []);
          }
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
          this.errorMessage = this.$t("productionPriority.loadError");
        });
    },
    reloadTimeline() {
      this.loadData({ silent: true });
    },
    // A timeline megmondhatja, hány nap hiányzik (kizoomolva sok kell egyszerre)
    loadMoreTimeline(days) {
      if (this.view !== "timeline" || this.timelineLoadingMore) return;
      const step = Number(days) > 0 ? Math.ceil(Number(days)) : 28;
      this.timelineLoadingMore = true;
      this.timelineRangeEnd = dayjs(this.timelineRangeEnd)
        .add(step, "day")
        .format("YYYY-MM-DD");
      http
        .fetch(
          `/production/priority/timeline?from=${this.timelineRangeStart}&to=${this.timelineRangeEnd}`
        )
        .then((data) => {
          if (!data) return;
          this.timelineDays = data.days || [];
          this.timelineBoards = data.boards || [];
        })
        .catch(() => {
          this.timelineRangeEnd = dayjs(this.timelineRangeEnd)
            .subtract(step, "day")
            .format("YYYY-MM-DD");
        })
        .finally(() => {
          this.timelineLoadingMore = false;
        });
    },
    switchView(view) {
      if (this.view === view) return;
      this.view = view;
      if (view !== "timeline") {
        this.timelineRangeStart = "";
        this.timelineRangeEnd = "";
      }
      this.loadData();
    },
    buildBoard(columns, cards) {
      const columnMap = {};
      columns.forEach((column) => {
        columnMap[column.column_key] = {
          ...column,
          cards: [],
        };
      });

      const cardMap = {};
      cards.forEach((card) => {
        cardMap[card.id] = card;
      });

      cards.forEach((card) => {
        const column = columnMap[card.column_key] || columnMap["unassigned:0"];
        if (column) {
          column.cards.push(card);
        }
      });

      Object.values(columnMap).forEach((column) => {
        column.cards.sort(
          (a, b) =>
            (a.production_priority === null
              ? Number.MAX_SAFE_INTEGER
              : a.production_priority) -
            (b.production_priority === null
              ? Number.MAX_SAFE_INTEGER
              : b.production_priority)
        );
      });

      this.columns = Object.values(columnMap);
      this.refreshPriorityLabels();
    },
    refreshPriorityLabels() {
      let counter = 0;
      this.columns.forEach((column) => {
        column.cards.forEach((card) => {
          card.priority_label = ++counter;
        });
      });
    },
    onColumnChange(column, event) {
      if (event.added) {
        const card = event.added.element;
        const newIndex =
          event.added.newIndex !== undefined
            ? event.added.newIndex
            : column.cards.findIndex((c) => c.id === card.id);
        card.column_key = column.column_key;
        this.updateCardLocation(card, column, newIndex);
      } else if (event.moved) {
        this.saveColumnOrder(column);
      }
      this.refreshPriorityLabels();
    },
    updateCardLocation(card, column, newIndex) {
      http
        .fetch("/production/priority/move", {
          operation_id: card.id,
          location_type: column.type,
          location_id: column.id,
          new_index: newIndex,
        })
        .catch(() => {
          this.loadData();
        });
    },
    saveColumnOrder(column) {
      http
        .fetch("/production/priority/reorder", {
          location_type: column.type,
          location_id: column.id,
          ordered_ids: column.cards.map((c) => c.id),
        })
        .catch(() => {
          this.loadData();
        });
    },
    columnTypeLabel(type) {
      if (type === "warehouse") return this.$t("productionPriority.warehouse");
      if (type === "partner") return this.$t("productionPriority.partner");
      return this.$t("productionPriority.unassigned");
    },
    queuedHours(column) {
      return column.cards.reduce(
        (sum, card) => sum + (card.estimated_hours || 0),
        0
      );
    },
    capacityHours(column) {
      return column.used_hours + this.queuedHours(column);
    },
    capacityRatio(column) {
      if (!column.capacity_hours) return 0;
      return this.capacityHours(column) / column.capacity_hours;
    },
    isOverdue(card) {
      if (!card.deadline_date) return false;
      return dayjs(card.deadline_date).isBefore(dayjs(), "day");
    },
    formatDate(value) {
      return dayjs(value).format("YYYY-MM-DD");
    },
    productImageUrl(image) {
      if (!image?.path || !image?.name) return null;
      return (
        (this.mediaServer || "") +
        "/" +
        image.path +
        "/262-" +
        image.name +
        ".webp"
      );
    },
    openCapacity(column) {
      this.capacityColumn = column;
      this.capacityWeeks = [];
      this.capacityBase = {
        minimum_hours: "",
        expected_hours: "",
        maximum_hours: "",
        confidence: "",
      };
      this.capacityLoading = true;
      this.capacitySaving = false;
      const from = dayjs().startOf("week").format("YYYY-MM-DD");
      const to = dayjs(from)
        .add(8 * 7, "day")
        .format("YYYY-MM-DD");
      http
        .fetch(
          `/production/priority/capacity?partner_id=${column.id}&from=${from}&to=${to}`
        )
        .then((data) => {
          this.capacityLoading = false;
          const base = data?.base || {};
          this.capacityBase = {
            minimum_hours: base.minimum_hours ?? "",
            expected_hours: base.expected_hours ?? "",
            maximum_hours: base.maximum_hours ?? "",
            confidence: base.confidence ?? "",
          };
          this.capacityWeeks = this.buildCapacityWeeks(
            from,
            data?.capacity || [],
            base
          );
          this.$refs.capacityModal.showModal();
        })
        .catch(() => {
          this.capacityLoading = false;
        });
    },
    buildCapacityWeeks(from, forecasts, base) {
      const forecastMap = {};
      forecasts.forEach((f) => {
        forecastMap[dayjs(f.week_start).format("YYYY-MM-DD")] = f;
      });
      const baseValues = base || {};
      const weeks = [];
      for (let i = 0; i < 8; i++) {
        const key = dayjs(from)
          .add(i * 7, "day")
          .format("YYYY-MM-DD");
        const f = forecastMap[key];
        weeks.push({
          week_start: key,
          forecast_id: f?.id ?? null,
          minimum_hours: f?.minimum_hours ?? baseValues.minimum_hours ?? "",
          expected_hours: f?.expected_hours ?? baseValues.expected_hours ?? "",
          maximum_hours: f?.maximum_hours ?? baseValues.maximum_hours ?? "",
          confidence: f?.confidence ?? baseValues.confidence ?? "",
          custom: !!f,
        });
      }
      return weeks;
    },
    markWeekCustom(week) {
      week.custom = true;
    },
    saveCapacity() {
      if (!this.capacityColumn) return;
      this.capacitySaving = true;
      const requests = [];
      const base = this.capacityBase;
      const baseExpected =
        base.expected_hours === "" || base.expected_hours == null
          ? ""
          : base.expected_hours;

      if (baseExpected === "") {
        requests.push(
          http.fetch("/production/priority/capacity/base/delete", {
            partner_id: this.capacityColumn.id,
          })
        );
      } else {
        requests.push(
          http.fetch("/production/priority/capacity/base", {
            partner_id: this.capacityColumn.id,
            minimum_hours:
              base.minimum_hours === "" || base.minimum_hours == null
                ? ""
                : base.minimum_hours,
            expected_hours: baseExpected,
            maximum_hours:
              base.maximum_hours === "" || base.maximum_hours == null
                ? ""
                : base.maximum_hours,
            confidence:
              base.confidence === "" || base.confidence == null
                ? ""
                : base.confidence,
          })
        );
      }

      this.capacityWeeks
        .filter((week) => week.custom)
        .forEach((week) => {
          requests.push(
            http.fetch("/production/priority/capacity", {
              partner_id: this.capacityColumn.id,
              week_start: week.week_start,
              minimum_hours:
                week.minimum_hours === "" || week.minimum_hours == null
                  ? ""
                  : week.minimum_hours,
              expected_hours:
                week.expected_hours === "" || week.expected_hours == null
                  ? ""
                  : week.expected_hours,
              maximum_hours:
                week.maximum_hours === "" || week.maximum_hours == null
                  ? ""
                  : week.maximum_hours,
              confidence:
                week.confidence === "" || week.confidence == null
                  ? ""
                  : week.confidence,
            })
          );
        });

      Promise.all(requests)
        .then(() => {
          this.capacitySaving = false;
          this.$refs.capacityModal.hideModal();
          this.loadData();
        })
        .catch(() => {
          this.capacitySaving = false;
        });
    },
    deleteCapacityWeek(week) {
      if (!week.forecast_id) return;
      http
        .fetch("/production/priority/capacity/delete", {
          id: week.forecast_id,
        })
        .then(() => {
          week.forecast_id = null;
          week.custom = false;
          week.minimum_hours = this.capacityBase.minimum_hours ?? "";
          week.expected_hours = this.capacityBase.expected_hours ?? "";
          week.maximum_hours = this.capacityBase.maximum_hours ?? "";
          week.confidence = this.capacityBase.confidence ?? "";
        });
    },
    closeCapacity() {
      if (this.$refs.capacityModal) {
        this.$refs.capacityModal.hideModal();
      }
    },
  },
};
</script>

<style scoped>
/* Gyártási prioritás tábla — a Prodyflow tokenjeivel (style.css :root) */
/* A gyökérre a TransitionPage.vue az afterEnter-ben inline `height: auto`-t
   tesz (minden oldalon), ezért a magasságot NEM ide, hanem a belső
   konténerre tesszük — különben elszáll a belső görgetés. */
.production-priority-page {
  font-family: var(--body-font);
  color: var(--black);
}
.pp-inner {
  padding: 14px 0 16px;
  height: calc(100vh - 120px);
  display: flex;
  flex-direction: column;
  min-height: 0;
}

/* ── FEJLÉC ── */
.priority-header {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
  margin-bottom: 12px;
}
.pp-title .regular-12 {
  margin-top: 2px;
}
.pp-stats {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.pp-stat {
  display: flex;
  align-items: baseline;
  gap: 7px;
  padding: 6px 11px;
  background: var(--white);
  border: 1px solid var(--divider);
  border-radius: 6px;
  white-space: nowrap;
}
.pp-stat-label {
  font-size: 0.6875rem;
  color: var(--gray);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
}
.pp-stat-value {
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--black);
}
.pp-refresh {
  display: flex;
  align-items: center;
  gap: 7px;
  white-space: nowrap;
}
.pp-refresh svg {
  width: 14px;
  height: 14px;
}

/* ── TÁBLA ── */
.board-scroll {
  flex: 1;
  min-height: 0;
  overflow-x: auto;
  overflow-y: hidden;
  padding-bottom: 4px;
}
.board {
  display: flex;
  align-items: stretch;
  gap: 12px;
  min-width: max-content;
  height: 100%;
}
.board-column {
  width: 320px;
  flex-shrink: 0;
  background: var(--white);
  border-radius: 8px;
  border-top: 3px solid var(--gray-light);
  box-shadow: 0 0 2px 1px rgba(202, 202, 202, 0.35);
  display: flex;
  flex-direction: column;
  min-height: 0;
}
.board-column--warehouse {
  border-top-color: var(--default);
}
.board-column--partner {
  border-top-color: var(--green);
}
.column-header {
  padding: 12px 14px;
  border-bottom: 1px solid var(--divider);
}
.column-title {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.column-name {
  flex: 1;
  min-width: 0;
  font-weight: 700;
  font-size: 0.9375rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.column-type {
  flex-shrink: 0;
  font-size: 0.5625rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  padding: 3px 7px;
  border-radius: 3px;
  background: var(--bg);
  color: var(--gray);
}
.column-type.type-warehouse {
  background: var(--disabled);
  color: var(--default);
}
.column-type.type-partner {
  background: var(--lgreen);
  color: #0e7a52;
}
.capacity-edit-btn {
  width: 26px;
  height: 26px;
  flex-shrink: 0;
  border-radius: 5px;
  border: 1px solid var(--divider);
  background: var(--white);
  color: var(--gray);
  display: grid;
  place-items: center;
  cursor: pointer;
  transition: 0.2s;
}
.capacity-edit-btn:hover {
  border-color: #a947d6;
  color: #a947d6;
}
.capacity-edit-btn svg {
  width: 13px;
  height: 13px;
}
.column-capacity {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: 7px;
  font-size: 0.6875rem;
  color: var(--gray);
}
.capacity-bar {
  height: 5px;
  border-radius: 3px;
  background: var(--divider-graph);
  margin-top: 8px;
  overflow: visible;
  position: relative;
}
.capacity-bar-fill {
  height: 100%;
  border-radius: 3px;
  background: var(--green);
}
/* 70%-os jelölő — efölött sárga, 100% felett piros */
.capacity-bar-mark {
  position: absolute;
  top: -2px;
  bottom: -2px;
  left: 70%;
  width: 1px;
  background: var(--gray-light);
}
.cap-sep {
  color: var(--gray-light);
}
.column-count {
  margin-left: auto;
  font-weight: 700;
  color: var(--gray);
  background: var(--bg);
  border-radius: 3px;
  padding: 2px 6px;
  white-space: nowrap;
}
.column-empty {
  border: 1px dashed var(--divider);
  border-radius: 8px;
  padding: 18px 12px;
  text-align: center;
  font-size: 0.75rem;
  color: var(--gray-light);
}
.card-more {
  font-size: 0.625rem;
  font-weight: 600;
  color: #713ae8;
  padding-left: 42px;
  margin-top: 6px;
}
.column-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* ── KÁRTYA ── */
.priority-card {
  background: var(--white);
  border: 1px solid var(--divider);
  border-left: 3px solid var(--divider);
  border-radius: 8px;
  padding: 10px;
  cursor: grab;
  transition: box-shadow 0.2s;
}
.priority-card:hover {
  box-shadow: 0 2px 8px rgba(37, 39, 51, 0.1);
}
.priority-card:active {
  cursor: grabbing;
}
.priority-card.is-overdue {
  background: #fffbfb;
  border-color: #f5c2c2;
}
.card-top {
  display: flex;
  align-items: center;
  gap: 7px;
  min-width: 0;
}
.card-priority {
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  border-radius: 5px;
  display: grid;
  place-items: center;
  font-size: 0.625rem;
  font-weight: 700;
}
.card-id {
  flex: 1;
  min-width: 0;
  font-size: 0.6875rem;
  font-weight: 600;
  color: var(--gray);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.card-overdue-badge {
  flex-shrink: 0;
  font-size: 0.5625rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  padding: 2px 6px;
  border-radius: 3px;
  background: #ffe3e3;
  color: var(--red);
}
.card-item {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  min-width: 0;
}
.card-image {
  width: 34px;
  height: 34px;
  flex-shrink: 0;
  border-radius: 6px;
  object-fit: cover;
  background: var(--extra-light);
  border: 1px solid var(--divider);
  color: var(--gray-light);
}
.card-item-info {
  flex: 1;
  min-width: 0;
}
.card-item-name {
  font-size: 0.75rem;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.card-item-meta {
  font-size: 0.625rem;
  color: var(--gray);
  margin-top: 1px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.card-footer {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 9px;
  padding-top: 8px;
  border-top: 1px solid var(--divider-graph);
}
.card-deadline {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.625rem;
  font-weight: 600;
  color: var(--gray);
  white-space: nowrap;
}
.card-deadline.is-overdue {
  color: var(--red);
}
.card-deadline svg {
  width: 11px;
  height: 11px;
}
.card-hours {
  margin-left: auto;
  flex-shrink: 0;
  font-size: 0.6875rem;
  font-weight: 700;
  color: var(--black);
  white-space: nowrap;
}

/* ── KAPACITÁS MODAL ── */
.base-capacity-section {
  background: var(--bg);
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 16px;
}
.base-capacity-title {
  margin-bottom: 8px;
}
.capacity-weeks-title {
  margin-bottom: 4px;
}
.week-is-base {
  background: var(--extra-light);
}
.week-is-base input {
  color: var(--gray);
}
</style>
